import React, { useState, useCallback, useEffect, useRef } from "react";

import { useLocalization } from "@fluent/react";
import {
  Alert,
  Button,
  Chip,
  Radio,
  Row,
  Select,
  TextInput,
} from "@narmi/design_system";
import Dialog from "cerulean/src/Dialog";

import {
  ACCOUNTING_FORMAT_OPTIONS,
  DEFAULT_ACCOUNTING_FORMAT,
} from "../../constants";

import styles from "./ExportDialog.module.scss";

import type {
  AccountingFormat,
  ExportDownloadParams,
  ExportFormat,
} from "../../constants";

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: (params: ExportDownloadParams) => void;
  isTooLarge?: boolean;
  isExporting?: boolean;
}

const ExportDialog = ({
  isOpen,
  onClose,
  onDownload,
  isTooLarge = false,
  isExporting = false,
}: ExportDialogProps) => {
  const { l10n } = useLocalization();
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(
    null
  );
  const [accountingFormat, setAccountingFormat] = useState<AccountingFormat>(
    DEFAULT_ACCOUNTING_FORMAT
  );
  const [fileName, setFileName] = useState("");
  const tooLargeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFormat(null);
      setAccountingFormat(DEFAULT_ACCOUNTING_FORMAT);
      setFileName("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (isTooLarge && tooLargeRef.current) {
      tooLargeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [isTooLarge]);

  const handleFormatChange = useCallback((format: ExportFormat) => {
    setSelectedFormat(format);
  }, []);

  const handleFileNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFileName(e.target.value);
    },
    []
  );

  const handleDownload = useCallback(() => {
    if (!selectedFormat || !fileName.trim()) return;
    onDownload({
      format: selectedFormat,
      accountingFormat:
        selectedFormat === "accounting_software" ? accountingFormat : null,
      fileName: fileName.trim(),
    });
  }, [selectedFormat, accountingFormat, fileName, onDownload]);

  const isDownloadDisabled = !selectedFormat || !fileName.trim() || isExporting;

  return (
    <Dialog
      isOpen={isOpen}
      title={l10n.getString("live-ledger-export-dialog-title")}
      headerStyle="bordered"
      onUserDismiss={onClose}
      width="570px"
      testId="export-dialog"
      footer={
        <Row alignItems="center" justifyContent="end" gapSize="m">
          <Row.Item shrink>
            <div className={styles.cancelButton}>
              <Button
                kind="secondary"
                onClick={onClose}
                startIcon="x"
                label={l10n.getString("live-ledger-export-dialog-cancel")}
                testId="export-dialog-cancel"
              />
            </div>
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="primary"
              onClick={handleDownload}
              label={l10n.getString("live-ledger-export-dialog-download")}
              startIcon="download"
              disabled={isDownloadDisabled}
              testId="export-dialog-download"
            />
          </Row.Item>
        </Row>
      }
    >
      <p className={styles.subtitle}>
        {l10n.getString("live-ledger-export-dialog-subtitle")}
      </p>

      <div className={styles.banner}>
        <Alert isActive kind="info" isDismissable={false}>
          {l10n.getString("live-ledger-export-dialog-banner")}
        </Alert>
      </div>

      <div
        className={styles.formatOptions}
        role="radiogroup"
        aria-label={l10n.getString("live-ledger-export-dialog-title")}
      >
        <Radio
          kind="input-card"
          name="exportFormat"
          value="bai2"
          checked={selectedFormat === "bai2"}
          onCheck={() => handleFormatChange("bai2")}
        >
          <div className={styles.formatHeader}>
            <span className="fontWeight--semibold">
              {l10n.getString("live-ledger-export-dialog-format-bai2")}
            </span>
            <Chip
              kind="success"
              label={l10n.getString("live-ledger-export-dialog-recommended")}
            />
          </div>
          <p className={styles.formatDescription}>
            {l10n.getString("live-ledger-export-dialog-bai2-description")}
          </p>
        </Radio>

        <Radio
          kind="input-card"
          name="exportFormat"
          value="csv"
          checked={selectedFormat === "csv"}
          onCheck={() => handleFormatChange("csv")}
        >
          <span className="fontWeight--semibold">
            {l10n.getString("live-ledger-export-dialog-format-csv")}
          </span>
          <p className={styles.formatDescription}>
            {l10n.getString("live-ledger-export-dialog-csv-description")}
          </p>
        </Radio>

        <Radio
          kind="input-card"
          name="exportFormat"
          value="zip"
          checked={selectedFormat === "zip"}
          onCheck={() => handleFormatChange("zip")}
        >
          <span className="fontWeight--semibold">
            {l10n.getString("live-ledger-export-dialog-format-zip")}
          </span>
          <p className={styles.formatDescription}>
            {l10n.getString("live-ledger-export-dialog-zip-description")}
          </p>
        </Radio>

        <Radio
          kind="input-card"
          name="exportFormat"
          value="accounting_software"
          checked={selectedFormat === "accounting_software"}
          onCheck={() => handleFormatChange("accounting_software")}
        >
          <span className="fontWeight--semibold">
            {l10n.getString("live-ledger-export-dialog-format-accounting")}
          </span>
          <p className={styles.formatDescription}>
            {l10n.getString("live-ledger-export-dialog-accounting-description")}
          </p>
          {selectedFormat === "accounting_software" && (
            <div className={styles.accountingFormatSelect}>
              <Select
                label={l10n.getString(
                  "live-ledger-export-dialog-accounting-format-label"
                )}
                value={accountingFormat}
                onChange={(value: string) =>
                  setAccountingFormat(value as AccountingFormat)
                }
              >
                {ACCOUNTING_FORMAT_OPTIONS.map((opt) => (
                  <Select.Item key={opt.value} value={opt.value}>
                    {l10n.getString(opt.ftlKey)}
                  </Select.Item>
                ))}
              </Select>
            </div>
          )}
        </Radio>
      </div>

      <div className={styles.fileNameSection}>
        <div>
          <span className={styles.fileNameLabel}>
            {l10n.getString("live-ledger-export-dialog-filename-label")}
          </span>
          <TextInput
            aria-label={l10n.getString(
              "live-ledger-export-dialog-filename-label"
            )}
            value={fileName}
            onChange={handleFileNameChange}
          />
        </div>
        {isTooLarge && (
          <div ref={tooLargeRef} className={styles.tooLargeError}>
            <Alert isActive kind="error" isDismissable={false}>
              {l10n.getString("live-ledger-export-dialog-too-large")}
            </Alert>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default ExportDialog;
